import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoGridOutline } from 'react-icons/io5';

const COLUMN_ORDER = ['outward', 'with_customer', 'inward', 'inspection', 'laundry', 'ready', 'damaged'];

export default function AdminRentalKanban() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    api('/api/admin/rental/kanban')
      .then((d) => { setData(d); setError(''); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  if (loading) return <AdminLoading />;

  const columns = COLUMN_ORDER.map((id) => data?.[id]).filter(Boolean);

  return (
    <Card>
      <CardHeader title="Rental Kanban" subheader="Rental lifecycle overview" />
      <CardContent>
        {error && <Typography color="error" className="admin-error" sx={{ mb: 2 }}>{error}</Typography>}
        {columns.length === 0 ? (
          <AdminEmptyState message="No rental data." icon={IoGridOutline} />
        ) : (
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
            {columns.map((col) => (
              <Paper
                key={col.label}
                variant="outlined"
                sx={{
                  minWidth: 220,
                  maxWidth: 260,
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '70vh',
                }}
              >
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" fontWeight={600}>{col.label}</Typography>
                  <Chip label={col.count} size="small" />
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                  {(col.cards || []).map((c, i) => (
                    <Paper
                      key={c.orderItemId || c.productId || i}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        bgcolor: 'action.hover',
                        cursor: 'default',
                      }}
                    >
                      {c.orderId && <Typography variant="caption" color="text.secondary">Order #{c.orderId}</Typography>}
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>{c.productName || '–'}</Typography>
                      {c.contactName && <Typography variant="caption" display="block">{c.contactName}</Typography>}
                      {(c.eventDate || c.returnBy) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {c.eventDate && `Event: ${c.eventDate}`}
                          {c.returnBy && ` · Return: ${c.returnBy}`}
                        </Typography>
                      )}
                      {c.notes && <Typography variant="caption" color="error" display="block">{c.notes}</Typography>}
                    </Paper>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
